import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/virtualizedTable";
import { createColumnHelper } from "@tanstack/react-table";
import { Trash2, Users, Building } from "lucide-react";
import { BeaconFundRequest, useDeleteBeaconRequest } from "@/lib/api/fund";
import { formatCurrency } from "@/lib/utils";
import { getReadableStatusLabel, getStatusBadgeColor } from "../../common";
import { LoadingIndicator } from "../components/LoadingIndicator";
import { DeleteConfirmationDialog } from "../components/DeleteConfirmationDialog";
import { useToast } from "@/components/ui/use-toast";

interface BeaconFundRequestsTableProps {
  fundRequests: BeaconFundRequest[];
  isLoading: boolean;
}

export function BeaconFundRequestsTable({ fundRequests, isLoading }: BeaconFundRequestsTableProps) {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteRequestId, setDeleteRequestId] = useState<string | null>(null);
  const [requestToDelete, setRequestToDelete] = useState<BeaconFundRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const columnHelper = createColumnHelper<BeaconFundRequest>();
  
  const deleteBeaconRequest = useDeleteBeaconRequest();

  const handleDelete = (request: BeaconFundRequest) => {
    setRequestToDelete(request);
    setDeleteRequestId(request.id);
  };

  const confirmDelete = async () => {
    if (!deleteRequestId) return;
    
    setIsDeleting(true);
    try {
      const result = await deleteBeaconRequest({ id: deleteRequestId });
      if (result.success) {
        toast({ title: "Request deleted successfully" });
        setDeleteRequestId(null);
        setRequestToDelete(null);
      } else {
        throw new Error("Delete failed");
      }
    } catch (error) {
      toast({ 
        title: "Failed to delete request",
        variant: "destructive" 
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const setSpecificParam = (paramName: string, paramValue: string) => {
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set(paramName, paramValue);
      return newParams;
    });
  };

  const columns = [
    columnHelper.accessor("survey.data.firstName", {
      id: "recipient",
      header: "Recipient",
      meta: { isCellClick: true },
      ...({ accessorFn: (row: any) => `${row.survey.data?.firstName} ${row.survey.data?.lastName}` }),
      cell: (info) => (
        <div>
          <div className="font-medium">
            {info.row.original.survey.data.firstName} {info.row.original.survey.data.lastName}
          </div>
          <div className="text-sm text-muted-foreground">
            {info.row.original.survey.data.email}
          </div>
        </div>
      ),
    }),
    columnHelper.accessor("nominated_organization", {
      id: "organization",
      header: "Organization",
      meta: { isCellClick: true },
      ...({ accessorFn: (row: any) => row.survey.data?.recipientOrganization }),
      cell: (info) => (
        <div>
          <div className="font-medium">{info.getValue()}</div>
          <div className="text-sm text-muted-foreground">
            {info.getValue()}
          </div>
        </div>
      ),
    }),
    columnHelper.accessor("Customer", {
      id: "customer",
      header: "Customer/Employee",
      meta: { isCellClick: true },
      ...({ accessorFn: (row: any) => row.Customer?.long_name || row.Employee?.name || row.customer_requested || "Unassigned" }),
      cell: (info: any) => {
        const customer = info.row.original.Customer;
        const employee = info.row.original.Employee;
        return (
          <div className="flex items-center gap-2">
            {customer ? (
              <>
                <Building className="w-4 h-4 text-muted-foreground" />
              </>
            ) : employee ? (
              <>
                <Users className="w-4 h-4 text-muted-foreground" />
              </>
            ) : null}
              <span>{info.getValue()}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: "Status",
      meta: { isCellClick: true },
      cell: (info) => (
        <Badge className={getStatusBadgeColor(info.getValue())}>
          {getReadableStatusLabel(info.getValue())}
        </Badge>
      ),
    }),
    columnHelper.accessor("amount_granted", {
      id: "amount",
      header: "Amount",
      meta: { isCellClick: true },
      cell: (info) => (
        <div>
          <div className="font-medium">
            {info.getValue() ? formatCurrency(parseFloat(info.getValue())) : "-"}
          </div>
          <div className="text-sm text-muted-foreground">
            Match: {info.row.original.survey.data.matchingGiftAmount || "-"}
          </div>
        </div>
      ),
    }),
    columnHelper.accessor("has_been_granted", {
      id: "granted",
      header: "Granted",
      meta: { isCellClick: true },
      ...({ accessorFn: (row: any) => row.has_been_granted ? "Yes" : "No" }),
      cell: (info: any) => {
        const hasBeen = info.row.original.has_been_granted;
        return (
          <Badge variant={hasBeen ? "default" : "secondary"}>
            {info.getValue()}
          </Badge>
        )
      },
    }),
    columnHelper.accessor("has_been_dispersed", {
      id: "dispersed",
      header: "Disbursed",
      meta: { isCellClick: true },
      ...({ accessorFn: (row: any) => row.has_been_dispersed ? "Yes" : "No" }),
      cell: (info) => {
        const hasBeen = info.row.original.has_been_dispersed;
        return (
          <Badge variant={hasBeen ? "default" : "secondary"}>
            {info.getValue()}
          </Badge>
        )
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(info.row.original);
          }}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      ),
    }),
  ];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Fund Requests <LoadingIndicator isLoading={isLoading} /></CardTitle>
        <CardDescription className="text-sm">
          Manage and review all beacon fund requests
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        <DataTable
          onCellClick={(row) => setSpecificParam("request_id", row.id)}
          defaultSorting={[{ id: "recipient", desc: false }]}
          columns={columns as any[]}
          data={fundRequests}
          height="calc(100vh - 380px)"
          emptyResultsMessage={isLoading ? "Loading rows..." : "No results."}
        />
      </CardContent>
      <DeleteConfirmationDialog
        isOpen={!!deleteRequestId}
        onClose={() => {
          setDeleteRequestId(null);
          setRequestToDelete(null);
        }}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        itemName={requestToDelete ? `${requestToDelete.survey.data.firstName} ${requestToDelete.survey.data.lastName}'` : ""}
      />
    </Card>
  );
}
